import React, { useEffect, useState } from 'react';
import { useDeleteAccount } from '@/hooks/api';
import { useUI } from '@/store';
import { TelegramUtils } from '@/utils/telegram';

const SettingsScreen: React.FC = () => {
  const { setActiveScreen } = useUI();
  const deleteAccount = useDeleteAccount();
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    TelegramUtils.configureBackButton(true, () => {
      setActiveScreen('profile');
    });
    TelegramUtils.configureMainButton({ visible: false });
  }, [setActiveScreen]);

  const handleDeleteAccount = async () => {
    console.log('🗑️ Delete account button clicked');
    
    // Show confirmation dialog
    const confirmed = await TelegramUtils.showConfirm(
      '⚠️ Удалить профиль?\n\nЭто действие необратимо. Все ваши данные, совпадения и сообщения будут удалены навсегда.'
    );

    console.log('First confirmation:', confirmed);

    if (confirmed) {
      // Second confirmation for safety
      const doubleConfirmed = await TelegramUtils.showConfirm(
        'Вы уверены? Это действие нельзя отменить!'
      );

      console.log('Second confirmation:', doubleConfirmed);

      if (doubleConfirmed) {
        console.log('🚀 Starting account deletion...');
        setIsDeleting(true);
        TelegramUtils.haptic.heavy();
        
        try {
          console.log('📡 Calling deleteAccount.mutateAsync()...');
          await deleteAccount.mutateAsync();
          console.log('✅ Delete account mutation completed');
        } catch (error) {
          console.error('❌ Delete account error:', error);
          setIsDeleting(false);
        }
      } else {
        console.log('❌ Second confirmation cancelled');
      }
    } else {
      console.log('❌ First confirmation cancelled');
    }
  };

  return (
    <div className="min-h-screen bg-tg-bg safe-area-y">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <button
          onClick={() => {
            setActiveScreen('profile');
            TelegramUtils.haptic.light();
          }}
          className="p-2 -ml-2 hover:bg-white/5 rounded-xl transition-colors"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <h1 className="text-xl font-bold text-white absolute left-1/2 transform -translate-x-1/2">Настройки</h1>
        
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="space-y-4">
          {/* Security Section */}
          <div>
            <h2 className="text-sm font-semibold text-white/50 light:text-neutral-500 uppercase tracking-wider mb-3 px-2">
              Безопасность
            </h2>
            
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden light:bg-neutral-100 light:border-neutral-300">
              {/* Blocked Users Button */}
              <button
                onClick={() => {
                  setActiveScreen('blocked-users');
                  TelegramUtils.haptic.light();
                }}
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 light:hover:bg-neutral-200 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-neutral-700/50 light:bg-neutral-300 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white light:text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-white light:text-neutral-900 font-medium">Заблокированные пользователи</p>
                    <p className="text-sm text-white/50 light:text-neutral-500">Управление списком блокировки</p>
                  </div>
                </div>
                <svg className="w-5 h-5 text-white/30 light:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Account Section */}
          <div>
            <h2 className="text-sm font-semibold text-white/50 light:text-neutral-500 uppercase tracking-wider mb-3 px-2">
              Аккаунт
            </h2>
            
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden light:bg-neutral-100 light:border-neutral-300">
              {/* Delete Account Button */}
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 light:hover:bg-neutral-200 transition-colors disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-danger-500/10 light:bg-danger-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-danger-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-white light:text-neutral-900 font-medium">Удалить профиль</p>
                    <p className="text-sm text-white/50 light:text-neutral-500">Действие необратимо</p>
                  </div>
                </div>
                <svg className="w-5 h-5 text-white/30 light:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Info Section */}
          <div className="mt-8">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 light:bg-neutral-100 light:border-neutral-300">
              <div className="flex items-start gap-3">
                <div className="text-2xl">ℹ️</div>
                <div>
                  <p className="text-sm text-white/70 light:text-neutral-700 leading-relaxed">
                    При удалении профиля будут безвозвратно удалены:
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-white/50 light:text-neutral-600">
                    <li>• Все ваши фото и данные профиля</li>
                    <li>• История свайпов и лайков</li>
                    <li>• Все совпадения и сообщения</li>
                    <li>• Результаты IT-Quest теста</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* App Info */}
          <div className="mt-8 text-center">
            <p className="text-xs text-white/30 light:text-neutral-400">
              ITinder v1.0.0
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsScreen;

